/**
 * Generate and download PDF for an invoice using browser print functionality
 * @param {Object} invoice - The invoice object
 * @param {string} invoiceNumber - Invoice number for filename
 */
export const generateInvoicePDF = (invoice, invoiceNumber) => {
  // Open new window for printing
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    throw new Error('Popup blocked. Please allow popups for PDF downloads.');
  }

  // Format currency
  const formatCurrency = (amount, currency = 'GBP') => {
    if (!amount) return '£0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: currency 
    }).format(numAmount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get line items
  const lineItems = invoice.lineItems || invoice.items || [];

  // Calculate subtotal from line items if not provided or if it's zero
  let subtotal = invoice.subtotal;
  if (!subtotal || parseFloat(subtotal) === 0) {
    subtotal = lineItems.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 1;
      const rate = parseFloat(item.rate || item.unitPrice || 0);
      return sum + (quantity * rate);
    }, 0);
  } else {
    subtotal = parseFloat(subtotal);
  }

  // Get tax (convert to number)
  const tax = parseFloat(invoice.tax || 0);

  // Get early payment discount
  const discountAmount = parseFloat(invoice.discountAmount || 0);

  // Get refund amount
  const refundAmount = parseFloat(invoice.refundAmount || 0);

  // Calculate total (use provided total, or calculate from subtotal + tax - discount)
  let total = invoice.total;
  if (!total || parseFloat(total) === 0) {
    total = subtotal + tax - discountAmount;
  } else {
    total = parseFloat(total);
  }

  // Adjust total if there's a refund (show original total minus refund)
  const finalTotal = total - refundAmount;

  // Build sender info
  const senderName = invoice.sender?.companyName || `${invoice.sender?.firstName || ''} ${invoice.sender?.lastName || ''}`.trim() || 'N/A';
  const senderAddress = [
    invoice.sender?.address,
    invoice.sender?.city,
    invoice.sender?.state,
    invoice.sender?.zipCode,
    invoice.sender?.country
  ].filter(Boolean).join(', ') || 'N/A';
  const senderEmail = invoice.sender?.email || 'N/A';

  // Build receiver info
  let receiverName, receiverAddress, receiverEmail;
  if (invoice.guestRecipient) {
    // Guest recipient
    receiverName = invoice.guestRecipient.companyName || 
                  `${invoice.guestRecipient.firstName || ''} ${invoice.guestRecipient.lastName || ''}`.trim() || 
                  invoice.guestRecipient.name || 
                  'Guest Customer';
    receiverEmail = invoice.guestRecipient.email || 'N/A';
    receiverAddress = [
      invoice.guestRecipient.address,
      invoice.guestRecipient.city,
      invoice.guestRecipient.state,
      invoice.guestRecipient.zipCode,
      invoice.guestRecipient.country
    ].filter(Boolean).join(', ') || 'N/A';
  } else {
    // Registered user
    receiverName = invoice.receiver?.companyName || `${invoice.receiver?.firstName || ''} ${invoice.receiver?.lastName || ''}`.trim() || 'N/A';
    receiverAddress = [
      invoice.receiver?.address,
      invoice.receiver?.city,
      invoice.receiver?.state,
      invoice.receiver?.zipCode,
      invoice.receiver?.country
    ].filter(Boolean).join(', ') || 'N/A';
    receiverEmail = invoice.receiver?.email || 'N/A';
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice ${invoiceNumber}</title>
      <meta charset="UTF-8">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        * { box-sizing: border-box; margin: 0; padding: 0; }
        
        body { 
          font-family: 'Inter', 'Segoe UI', system-ui, sans-serif; 
          padding: 40px; 
          color: #1f2937;
          background: #ffffff;
          line-height: 1.6;
        }
        
        .invoice-container {
          max-width: 800px;
          margin: 0 auto;
          background: white;
        }
        
        .invoice-header {
          text-align: center;
          padding: 40px 0;
          border-bottom: 2px solid #e5e7eb;
          margin-bottom: 40px;
        }
        
        .invoice-title {
          font-size: 36px;
          font-weight: 300;
          color: #111827;
          margin-bottom: 12px;
          letter-spacing: 2px;
        }
        
        .invoice-number {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }
        
        .company-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          margin-bottom: 40px;
        }
        
        .company-card {
          background: #f9fafb;
          padding: 24px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
        
        .company-card h3 {
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 16px;
        }
        
        .company-card p {
          margin: 4px 0;
          font-size: 13px;
          color: #374151;
          line-height: 1.6;
        }
        
        .company-card p:first-of-type {
          font-weight: 600;
          color: #111827;
          font-size: 15px;
          margin-bottom: 8px;
        }
        
        .invoice-details {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 40px;
          padding: 24px;
          background: #f9fafb;
          border-radius: 8px;
        }
        
        .detail-item {
          text-align: center;
        }
        
        .detail-label {
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 6px;
        }
        
        .detail-value {
          font-size: 14px;
          font-weight: 500;
          color: #111827;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 40px;
        }
        
        thead {
          background: #f3f4f6;
        }
        
        th {
          padding: 12px;
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 1px;
          border-bottom: 2px solid #e5e7eb;
        }
        
        th:last-child { text-align: right; }
        
        td {
          padding: 14px 12px;
          font-size: 13px;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }
        
        td:last-child { 
          text-align: right;
          font-weight: 500;
        }
        
        tbody tr:hover {
          background: #f9fafb;
        }
        
        .totals-section {
          margin-left: auto;
          width: 300px;
          padding: 24px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 40px;
        }
        
        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          font-size: 14px;
          color: #374151;
        }
        
        .total-row.grand-total {
          padding-top: 16px;
          margin-top: 16px;
          border-top: 2px solid #e5e7eb;
          font-weight: 600;
          font-size: 16px;
          color: #111827;
        }
        
        .notes-section {
          margin-top: 40px;
          padding: 24px;
          background: #f9fafb;
          border-radius: 8px;
          border-left: 4px solid #3b82f6;
        }
        
        .notes-section h4 {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
        }
        
        .notes-section p {
          font-size: 13px;
          color: #374151;
          line-height: 1.6;
          white-space: pre-wrap;
        }
        
        @media print {
          body { padding: 0; }
          .invoice-container { box-shadow: none; }
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <div class="invoice-header">
          <div class="invoice-title">INVOICE</div>
          <div class="invoice-number">${invoiceNumber}</div>
        </div>
        
        <div class="company-section">
          <div class="company-card">
            <h3>From</h3>
            <p>${senderName}</p>
            <p>${senderAddress}</p>
            <p>${senderEmail}</p>
          </div>
          
          <div class="company-card">
            <h3>To</h3>
            <p>${receiverName}</p>
            <p>${receiverAddress}</p>
            <p>${receiverEmail}</p>
          </div>
        </div>
        
        <div class="invoice-details">
          <div class="detail-item">
            <div class="detail-label">Issue Date</div>
            <div class="detail-value">${formatDate(invoice.issueDate || invoice.createdAt)}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Due Date</div>
            <div class="detail-value">${formatDate(invoice.dueDate)}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Status</div>
            <div class="detail-value" style="text-transform: capitalize;">${invoice.status || 'Pending'}</div>
          </div>
          ${invoice.poNumber ? `
          <div class="detail-item">
            <div class="detail-label">PO Number</div>
            <div class="detail-value">${invoice.poNumber}</div>
          </div>
          ` : ''}
          ${invoice.status === 'paid' && invoice.paymentDate ? `
          <div class="detail-item">
            <div class="detail-label">Payment Date</div>
            <div class="detail-value">${formatDate(invoice.paymentDate)}</div>
          </div>
          ` : ''}
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th style="text-align: center; width: 80px;">Quantity</th>
              <th style="text-align: right; width: 100px;">Rate</th>
              <th style="text-align: right; width: 120px;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${lineItems.map(item => `
              <tr>
                <td>${item.description || 'N/A'}</td>
                <td style="text-align: center;">${item.quantity || 1}</td>
                <td style="text-align: right;">${formatCurrency(item.rate || item.unitPrice, invoice.currency)}</td>
                <td style="text-align: right;">${formatCurrency((item.quantity || 1) * (item.rate || item.unitPrice || 0), invoice.currency)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals-section">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>${formatCurrency(subtotal, invoice.currency)}</span>
          </div>
          <div class="total-row">
            <span>Tax:</span>
            <span>${formatCurrency(tax, invoice.currency)}</span>
          </div>
          ${discountAmount > 0 ? `
          <div class="total-row" style="background: #f0fdf4; margin: 8px -24px; padding: 8px 24px; border-top: 1px dashed #86efac; border-bottom: 1px dashed #86efac;">
            <span style="color: #15803d; font-style: italic;">
              ✨ Early Payment Discount${invoice.discountSourceInvoice?.invoiceNumber ? `<br><span style="font-size: 11px; font-style: normal;">(from ${invoice.discountSourceInvoice.invoiceNumber})</span>` : ''}:
            </span>
            <span style="color: #15803d; font-style: italic; font-weight: 500;">-${formatCurrency(discountAmount, invoice.currency)}</span>
          </div>
          ` : ''}
          ${refundAmount > 0 ? `
          <div class="total-row" style="background: #fef2f2; margin: 8px -24px; padding: 8px 24px; border-top: 1px dashed #fca5a5; border-bottom: 1px dashed #fca5a5;">
            <span style="color: #b91c1c; font-style: italic;">
              ↩ Refunded Amount${invoice.refundDate ? `<br><span style="font-size: 11px; font-style: normal;">(${formatDate(invoice.refundDate)})</span>` : ''}:
            </span>
            <span style="color: #b91c1c; font-style: italic; font-weight: 500;">-${formatCurrency(refundAmount, invoice.currency)}</span>
          </div>
          ` : ''}
          <div class="total-row grand-total">
            <span>Total:</span>
            <span>${formatCurrency(finalTotal, invoice.currency)}</span>
          </div>
        </div>
        
        ${invoice.notes ? `
        <div class="notes-section">
          <h4>Notes</h4>
          <p>${invoice.notes}</p>
        </div>
        ` : ''}
        
        ${invoice.terms ? `
        <div class="notes-section" style="margin-top: 24px; border-left-color: #10b981;">
          <h4>Terms & Conditions</h4>
          <p>${invoice.terms}</p>
        </div>
        ` : ''}
      </div>
      
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
          }, 250);
        };
        
        window.onafterprint = function() {
          window.close();
        };
      </script>
    </body>
    </html>
  `);
  
  printWindow.document.close();
};

